import { useState } from 'react'
import { getFormattedDate } from 'src/utils/date-formatter.util'
import { TPlanningPatientMedEvent } from '../planning.type'
import { useTranslation } from 'react-i18next'
import { getDoctorCivilityAndName, getPatientCivilityAndFullName } from 'src/utils/string-formatter.util'
import { getTranslatedLabel } from 'src/utils/locale.util'
import { TNavigationLocales } from 'src/types/locales'
import { PatientPhone } from 'src/components/patient/patient-phone.component'
import { useSession } from 'src/providers/auth.provider'
import { AdminOnlyBadge } from 'src/components/admin-only-badge.component'
import {
  useCurrentProject,
  WithFeatureEnabled,
  WithPermission,
  WithSpecialAuthorization,
} from 'src/providers/current-project.provider'
import { useModals } from 'src/providers/modals.provider'
import {
  EPermissionGrantActions,
  EPermissionGrantSubjects,
  EPermissionSpecialAuthorization,
} from 'src/pages/configuration/permissions/_components/permissions.type'
import {
  AlertIcon,
  CurecallButton,
  CurecallDropdownButton,
  CurecallDropdownItemStyle,
  CurecallTag,
  CurecallTooltip,
  ECurecallSize,
  NoteIcon,
  PenIcon,
  RoundCheckIcon,
} from '@honestica/curecall-ui'
import { isPatientFollowupDocumentAvailable } from 'src/pages/patients/patient-file/patient-followup/_components/patient-forms/use-patient-followup-document-display.hook'
import { PatientDocumentsDrawer } from 'src/components/patient-documents-drawer/patient-documents-drawer.component'
import { PatientFollowupDocumentRow } from 'src/components/patient-documents-drawer/patient-followup-document-row.component'
import { twMerge } from 'src/utils/tw-merge.util'
import Link from 'src/components/navigation/link.component'
import MedicalAlertList from 'src/components/medical-alert-list/medical-alert-list.component'
import { TimezoneAwareTime } from 'src/components/timezone-aware-time/timezone-aware-time.component'

const PlanningIdCell = ({ patientMedEvent }: { patientMedEvent: TPlanningPatientMedEvent }) => {
  return <td>{patientMedEvent.Patient.remoteId || patientMedEvent.Patient.id}</td>
}

const PlanningPatientCell = ({ patientMedEvent }: { patientMedEvent: TPlanningPatientMedEvent }) => {
  const { t } = useTranslation()
  const birthdate = getFormattedDate(patientMedEvent.Patient.birthdate)
  return (
    <td>
      <p className="c-p-0 c-m-0 group-hover:underline group-hover:c-underline group-hover:c-text-primary c-text-grey-700">
        {getPatientCivilityAndFullName(t, patientMedEvent.Patient)}
      </p>
      {birthdate && (
        <p className="c-p-0 c-m-0 c-text-grey-700">
          {t('pages.patient-list-page.born-on')} {birthdate}
        </p>
      )}
      <PatientPhone patient={patientMedEvent.Patient} />
    </td>
  )
}

const PlanningMedEventCell = ({ patientMedEvent }: { patientMedEvent: TPlanningPatientMedEvent }) => {
  const { i18n } = useTranslation()
  const locale = i18n.language as TNavigationLocales

  const name = getTranslatedLabel(locale, patientMedEvent?.MedEvent?.intlName) || ''
  const medEventOriginalLabel = patientMedEvent?.metadata?.originalLabel
  const medicalStaffMemberName = getDoctorCivilityAndName(patientMedEvent?.MedicalStaffMember, false)

  return (
    <td>
      <div>{name}</div>
      {medEventOriginalLabel && <div>{medEventOriginalLabel}</div>}
      <div>
        <TimezoneAwareTime date={patientMedEvent.date} formatter="dateWithHours" />
      </div>
      <div>{medicalStaffMemberName}</div>
    </td>
  )
}

const PlanningConvocationCell = ({ patientMedEvent }: { patientMedEvent: TPlanningPatientMedEvent }) => {
  const { t } = useTranslation()
  const { openMedEventModal } = useModals()

  if (!patientMedEvent.convocationDate) {
    return <td></td>
  }

  return (
    <td>
      <div className="c-flex c-items-center c-gap-1 c-text-nowrap">
        <TimezoneAwareTime date={patientMedEvent.convocationDate} formatter="dateWithHours" />

        <WithPermission
          requiredSubject={EPermissionGrantSubjects.PATIENT}
          requiredAction={EPermissionGrantActions.UPDATE}
        >
          <CurecallButton
            iconType={<PenIcon />}
            isGhostButton={true}
            ariaLabelText={t('pages.planning.columns.update-convocation-date')}
            onClick={(event) => {
              event.stopPropagation()
              openMedEventModal({
                patientId: patientMedEvent.Patient.id,
                patientMedEventForForm: patientMedEvent,
              })
            }}
            className="tw-bg-primary-200 hover:tw-cursor-wait hover:tw-bg-primary hover:tw-text-white active:tw-text-primary active:tw-bg-primary-200"
          />
        </WithPermission>
      </div>
    </td>
  )
}

const PlanningMedicalTeamCommentCell = ({
  patientMedEvent,
}: {
  patientMedEvent: TPlanningPatientMedEvent
}) => {
  const { t } = useTranslation()
  const { openMedicalTeamCommentsModal } = useModals()
  const hasMedicalTeamComments = !!patientMedEvent.Patient?.medicalTeamCommentCount

  return (
    <td>
      <CurecallButton
        iconType={
          <div className="c-relative">
            {patientMedEvent.Patient?.medicalTeamCommentCount > 0 && (
              <span className="c-text-white c--top-4 c--right-4 c-absolute c-bg-secondary-700 c-rounded-full c-font-bold c-text-sm c-w-5 c-h-5">
                {patientMedEvent.Patient.medicalTeamCommentCount}
              </span>
            )}
            <NoteIcon className="c-w-6 c--mt-1" />
          </div>
        }
        isGhostButton={true}
        ariaLabelText={
          hasMedicalTeamComments
            ? t('pages.patient-list-page.display-comments-icon')
            : t('pages.patient-list-page.disabled-comments-icon')
        }
        onClick={(event) => {
          event.stopPropagation()
          openMedicalTeamCommentsModal({
            patientId: patientMedEvent.Patient.id,
            patientFollowup: patientMedEvent.PatientFollowup,
          })
        }}
        className="tw-bg-primary-200 hover:tw-cursor-wait hover:tw-bg-primary hover:tw-text-white active:tw-text-primary active:tw-bg-primary-200 tw-w-10 tw-h-10"
      />
    </td>
  )
}

const PlanningMedicalAlertCell = ({ patientMedEvent }: { patientMedEvent: TPlanningPatientMedEvent }) => {
  return (
    <td>
      <MedicalAlertList
        medicalAlertList={patientMedEvent.PatientMedAlerts}
        className="c-max-w-[280px]"
        medAlertSize="SMALL"
        showPinnedOnly
        callback={(event) => {
          event.stopPropagation()
        }}
      />
    </td>
  )
}

const PlanningMedicalStaffMemberCell = ({
  patientMedEvent,
}: {
  patientMedEvent: TPlanningPatientMedEvent
}) => {
  return <td>{getDoctorCivilityAndName(patientMedEvent.MedicalStaffMember)}</td>
}

const PlanningDocumentsCell = ({ patientMedEvent }: { patientMedEvent: TPlanningPatientMedEvent }) => {
  const { t } = useTranslation()
  const { project } = useCurrentProject()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const documents = patientMedEvent.PatientFollowup.documents ?? []
  const availableDocumentsCount = documents.filter(isPatientFollowupDocumentAvailable).length
  const areAllDocumentsAvailable = availableDocumentsCount === documents.length

  const documentsBadge = (
    <button type="button" onClick={() => setIsDrawerOpen(true)}>
      <CurecallTag status={areAllDocumentsAvailable ? 'SUCCESS' : 'WARNING'} className="tw-cursor-pointer">
        <span className="c-flex c-items-center c-gap-2">
          {areAllDocumentsAvailable ? (
            <RoundCheckIcon className="tw-h-3.5 tw-w-3.5" />
          ) : (
            <AlertIcon className="c-h-3.5 c-w-3.5" />
          )}
          {t('pages.planning.columns.documents-badge', {
            received: availableDocumentsCount,
            total: documents.length,
          })}
        </span>
      </CurecallTag>
    </button>
  )

  return (
    <td>
      <WithFeatureEnabled featuresAccepted={['patient-documents']}>
        <WithSpecialAuthorization
          requiredSpecialAuthorization={EPermissionSpecialAuthorization.CAN_VIEW_SENSITIVE_DATA}
        >
          {documents.length > 0 && (
            <div onClick={(event) => event.stopPropagation()}>
              {/* the key remounts the tooltip when the drawer toggles, which resets its pinned and hovered state */}
              <CurecallTooltip
                key={String(isDrawerOpen)}
                size="auto"
                tooltipPosition="right"
                contentWrapperClassName="tw-w-80 tw-rounded-md tw-bg-white tw-p-4 [&>svg]:c-hidden"
                content={
                  <div className="c-flex c-flex-col c-gap-4">
                    {documents.map((document) => (
                      <PatientFollowupDocumentRow key={document.variableId} document={document} />
                    ))}
                  </div>
                }
              >
                {documentsBadge}
              </CurecallTooltip>
              {isDrawerOpen && (
                <PatientDocumentsDrawer
                  documents={documents}
                  patientId={patientMedEvent.Patient.id}
                  projectId={project.id}
                  onClose={() => setIsDrawerOpen(false)}
                />
              )}
            </div>
          )}
        </WithSpecialAuthorization>
      </WithFeatureEnabled>
    </td>
  )
}

const ButtonItem = ({
  name,
  onClick,
  isAdminOnly,
}: {
  name: string
  onClick: VoidFunction
  isAdminOnly?: boolean
}) => {
  return (
    <li>
      <button
        type="button"
        className={twMerge(CurecallDropdownItemStyle, 'c-text-start hover:c-text-grey-700')}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          event.preventDefault()
          event.stopPropagation()
          onClick()
        }}
      >
        {isAdminOnly ? (
          <span className="c-flex c-items-center c-gap-1">
            <AdminOnlyBadge />
            {name}
          </span>
        ) : (
          name
        )}
      </button>
    </li>
  )
}

const PlanningActionsCell = ({ patientMedEvent }: { patientMedEvent: TPlanningPatientMedEvent }) => {
  const { i18n, t } = useTranslation()
  const { currentUser } = useSession()
  const { project, hasPermission } = useCurrentProject()
  const locale = i18n.language as TNavigationLocales
  const {
    openFollowupModal,
    openConsentModal,
    openMedicalTeamCommentsModal,
    openArchiveModal,
    openDeleteTestPatientModal,
  } = useModals()

  const actionButtonItems = []
  const patient = patientMedEvent.Patient
  const userCanUpdatePatient = hasPermission(EPermissionGrantSubjects.PATIENT, EPermissionGrantActions.UPDATE)

  if (userCanUpdatePatient) {
    actionButtonItems.push(
      <ButtonItem
        name={t('pages.patient-list-page.schedule-appointments-for-followup-name', {
          followupName: getTranslatedLabel(locale, patientMedEvent.PatientFollowup.Followup?.intlName),
        })}
        key={`action-button-patient-followup-${patientMedEvent.PatientFollowup.id}-${patient.id}`}
        onClick={() =>
          openFollowupModal({
            patientId: patient.id,
            patientFollowupIdToEdit: patientMedEvent.PatientFollowup.id,
          })
        }
      />,
    )
  }

  if (userCanUpdatePatient && project.consentNeeded && !patient?.PatientConsent?.consentStatus) {
    actionButtonItems.push(
      <ButtonItem
        name={t('pages.patient-list-page.obtain-patient-content')}
        key={`action-button-consent-${patient.id}`}
        onClick={() => openConsentModal({ patientId: patient.id })}
      />,
    )
  }
  actionButtonItems.push(
    <ButtonItem
      name={t('pages.patient-list-page.medical-team-comments')}
      key={`action-button-patient-comments-${patient.id}`}
      onClick={() =>
        openMedicalTeamCommentsModal({
          patientId: patient.id,
          patientFollowup: patientMedEvent.PatientFollowup,
        })
      }
    />,
  )
  actionButtonItems.push(
    <li key={`action-button-patient-see-file-${patient.id}`}>
      <Link
        to={`/${locale}/${project.id}/medical/patients/${patient.id}/${patientMedEvent.PatientFollowup.id}`}
        className={twMerge(CurecallDropdownItemStyle, 'c-text-start hover:c-text-grey-700')}
      >
        {t('pages.patient-list-page.see-file')}
      </Link>
    </li>,
  )

  if (patient.isTestAccount && currentUser?.isAdmin) {
    actionButtonItems.push(
      <ButtonItem
        name={t('pages.patient-list-page.delete-test-patient')}
        key={`action-button-patient-is-test-account-${patient.id}`}
        onClick={() => openDeleteTestPatientModal({ patient })}
        isAdminOnly
      />,
    )
  }

  if (!patient.isTestAccount && userCanUpdatePatient) {
    actionButtonItems.push(
      <ButtonItem
        name={t('components.patient.archive-or-delete.archived-this-patient')}
        key={`action-button-patient-archive-${patient.id}`}
        onClick={() => openArchiveModal({ patient })}
      />,
    )
  }

  return (
    <td>
      {actionButtonItems.length > 0 && (
        <CurecallDropdownButton
          dropdownButtonLabel={t('pages.patient-list-page.action')}
          size={ECurecallSize.SMALL}
          customItems={actionButtonItems}
          onClick={(event: React.MouseEvent<HTMLElement>) => {
            event.stopPropagation()
          }}
        />
      )}
    </td>
  )
}

export {
  PlanningIdCell,
  PlanningPatientCell,
  PlanningMedEventCell,
  PlanningConvocationCell,
  PlanningMedicalTeamCommentCell,
  PlanningMedicalAlertCell,
  PlanningMedicalStaffMemberCell,
  PlanningDocumentsCell,
  PlanningActionsCell,
}
